// src/App.js
//
// Two areas on one origin (Kuestion Separation contract v2.1):
//   apex      — the website, the Riftworks sign-in and the portal; session
//               namespace rw.suite.*
//   Kuestion  — everything under the Kuestion mount point, with its own
//               sign-in and session namespace rw.kuestion.* (KuestionArea)
// Neither area reads or clears the other's session.
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import MasterPage from "./components/landingpage/MasterPage";
import Login from "./components/loginpage/Login";
import VerificationSuccess from "./components/loginpage/verificationsuccess/verificationsuccess";
import VerificationProblem from "./components/loginpage/verificationproblem/VerificationProblem";
import ResetPassword from "./components/loginpage/resetpassword/ResetPassword";
import PortalBman from "./components/backupman/portal_bman";
import KuestionArea from "./components/kuestion/KuestionArea";
import ProtectedRoute from "./components/ProtectedRoute";
import { kq, KUESTION_MOUNT } from "./config/routes";
import { DOWNLOADS_PATH } from "./config/downloads";
import { TERMINALX_PATH } from "./config/terminalx";

import "./index.css";

// Kuestion is the only product with users, so the apex sign-in must never be
// where a Kuestion user ends up: a session made here cannot open Kuestion
// (separate namespaces) and the only place it leads is the BackupMan portal.
// That is exactly what happened to new accounts while the emailed links still
// point at the apex: verify → /login → /portal.
//
// So /login hands over to the Kuestion door, carrying ?reset=1 / ?forgot=1
// with it. The Riftworks sign-in itself is still here for staff, at
// /login?suite=1, and is what the contract calls the apex door.
const LoginRoute = () => {
  const { search } = useLocation();
  const wantsSuite = new URLSearchParams(search).has('suite');
  const { isAuthenticated, isLoading } = useAuth();
  if (!wantsSuite) return <Navigate to={`${kq('login')}${search}`} replace />;
  if (isLoading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', background: 'var(--color-bg, #f3f2f2)', color: 'var(--color-text, #201e1d)',
        fontFamily: 'var(--font-body, sans-serif)'
      }}>
        Loading...
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to="/portal" replace />;
  }
  return <Login area="suite" />;
};

// Student pages moved under the Kuestion mount point; old bookmarks follow.
const OldStudentRedirect = () => {
  const { pathname } = useLocation();
  const rest = pathname.split('/').slice(2).join('/');
  return <Navigate to={kq(rest ? `student/${rest}` : 'student')} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public landing page */}
      <Route path="/" element={<MasterPage />} />
      {/* Downloads: same page shell, its own address so the app can link to it */}
      <Route path={DOWNLOADS_PATH} element={<MasterPage />} />
      {/* TerminalX product page: same shell, its own address and colour theme */}
      <Route path={TERMINALX_PATH} element={<MasterPage />} />

      {/* Hands over to the Kuestion door; the Riftworks sign-in is /login?suite=1 */}
      <Route path="/login" element={<LoginRoute />} />

      {/* Emailed links that still point at the apex (every link, until the backend
          sets RWCORE_WEB_BASE_KUESTION; afterwards, non-Kuestion apptypes only) */}
      {/* area="kuestion": their way onward is the Kuestion sign-in, never /login → /portal */}
      <Route path="/verificationsuccess" element={<VerificationSuccess area="kuestion" />} />
      <Route path="/verificationexpired" element={<VerificationProblem kind="expired" area="kuestion" />} />
      <Route path="/verificationinvalid" element={<VerificationProblem kind="invalid" area="kuestion" />} />
      <Route path="/reset-password" element={<ResetPassword area="kuestion" />} />

      {/* The suite chooser is gone; old links land on the portal. */}
      <Route path="/suite" element={<Navigate to="/portal" replace />} />

      {/* BackupMan Portal */}
      <Route path="/portal" element={<ProtectedRoute><PortalBman /></ProtectedRoute>} />

      {/* Kuestion — its own door, session and auth pages */}
      <Route path={KUESTION_MOUNT} element={<KuestionArea />} />

      <Route path="/student/*" element={<OldStudentRedirect />} />

      {/* Unknown -> landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider area="suite">
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
